"use client";

import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type PlaylistSummary, type StationConfig } from "@/lib/api";
import { Icon, InfoNote, SettingsCard, Toggle } from "@/components/dashboard/settings-ui";

// Fine-tuning settings: small features that can be switched on or off without
// a code change (docs/backlog.md). Both live on the station's settings row.

export const TWEAKS_EVENT = "roa:station-tweaks-changed";

function useTweaks() {
  const [config, setConfig] = useState<StationConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .getStationConfig()
      .then(setConfig)
      .catch(() => setError("Couldn't load these settings."));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, [load]);

  async function save(patch: Partial<Pick<StationConfig, "fallback_enabled" | "fallback_playlist_id" | "broadcast_status_style">>) {
    if (!config) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.updateStationTweaks({
        fallback_enabled: config.fallback_enabled,
        fallback_playlist_id: config.fallback_playlist_id,
        broadcast_status_style: config.broadcast_status_style,
        ...patch,
      });
      setConfig(next);
      window.dispatchEvent(new Event(TWEAKS_EVENT));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return { config, error, busy, save };
}

export function FallbackCard({ canManage }: { canManage: boolean }) {
  const { config, error, busy, save } = useTweaks();
  const [playlists, setPlaylists] = useState<PlaylistSummary[]>([]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.listPlaylists().then(setPlaylists).catch(() => {});
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const chosen = config?.fallback_playlist_id ?? "";
  return (
    <SettingsCard
      icon={<Icon><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16M3 21v-5h5" /></Icon>}
      title="Fallback Playlist"
      subtitle="What the station plays when nothing else is on."
    >
      <div className="flex items-center gap-3">
        <Toggle
          checked={!!config?.fallback_enabled}
          disabled={!canManage || busy || !config || (!config.fallback_enabled && !chosen)}
          label="Use the fallback playlist"
          onChange={(v) => save({ fallback_enabled: v })}
        />
        <span className="text-sm">{config?.fallback_enabled ? "On" : "Off"}</span>
      </div>
      <select
        className="h-9 w-full rounded-lg border border-border-light bg-white px-3 text-sm disabled:bg-bg-main"
        value={chosen}
        disabled={!canManage || busy || !config}
        aria-label="Fallback playlist"
        onChange={(e) => save({ fallback_playlist_id: e.target.value || null, ...(e.target.value ? {} : { fallback_enabled: false }) })}
      >
        <option value="">Choose a playlist…</option>
        {playlists.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.is_smart ? " (smart)" : ""}
          </option>
        ))}
      </select>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[11px] text-accent-red">{error}</p>}
      <InfoNote>
        When a playlist finishes, or a show ends and nothing else is active, the station carries on with this playlist
        instead of going quiet. It loops, follows its own shuffle setting, and steps aside at the end of the current song
        for a scheduled show, an activated playlist or a live DJ. Song requests and jingle rotation work as normal. Turn
        it off to let the station go silent. Different from the emergency audio file, which only plays when the API is down.
      </InfoNote>
    </SettingsCard>
  );
}

export function HeaderStyleCard({ canManage }: { canManage: boolean }) {
  const { config, error, busy, save } = useTweaks();
  const style = config?.broadcast_status_style ?? "standard";
  const options = [
    { key: "standard" as const, label: "Standard", hint: "Blinking dot and text, e.g. “● Live · DJ Name”." },
    { key: "pill" as const, label: "Pill", hint: "A coloured “Live”, “Auto DJ” or “None” pill, with the DJ name beside it." },
  ];
  return (
    <SettingsCard
      icon={<Icon><circle cx="12" cy="12" r="3" /><path d="M7.8 16.2a6 6 0 0 1 0-8.4M16.2 7.8a6 6 0 0 1 0 8.4" /></Icon>}
      title="Broadcast Status Style"
      subtitle="How the header shows who is on air."
    >
      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Broadcast status style">
        {options.map((o) => (
          <label
            key={o.key}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
              style === o.key ? "border-accent-blue bg-blue-50/40" : "border-border-light"
            }`}
          >
            <input
              type="radio"
              name="broadcast-style"
              className="mt-1"
              checked={style === o.key}
              disabled={!canManage || busy || !config}
              onChange={() => save({ broadcast_status_style: o.key })}
            />
            <span>
              <span className="block text-sm font-medium">{o.label}</span>
              <span className="block text-[11px] text-text-tertiary">{o.hint}</span>
            </span>
          </label>
        ))}
      </div>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[11px] text-accent-red">{error}</p>}
    </SettingsCard>
  );
}
